/**
 * pipeline.js — Carousel Composer: the one pixel pipeline every graded surface
 * goes through (cell previews, the editor stage, the export).
 *
 * WHY THIS EXISTS. Adjustments used to be a CSS/canvas filter string. Highlights,
 * shadows, whites and blacks have no filter primitive at all, and `ctx.filter` is
 * missing on engines that still ship a canvas without it — those exports came out
 * ungraded, silently, and the app had to warn about it. Here the maths is ours, so
 * the preview and the file agree on every browser.
 *
 * ONE CONTEXT FOR THE WHOLE PAGE. A canvas per cell would exhaust the browser's
 * WebGL contexts (~8-16) within a few slides, and losing the oldest context is
 * silent. Everything renders through this module's single shared context and is
 * copied out into an ordinary 2D canvas, so callers can hold the result.
 *
 * COST. The shader is fill-rate bound and nothing else: on a machine falling back
 * to software GL it runs at roughly 75ns per pixel, so a live stage should be its
 * CSS size and not twice it. On any real GPU a full slide is a fraction of a
 * frame. The upload is skipped when the same ImageBitmap comes back, which is the
 * case while a slider is being dragged.
 *
 * ROTATION IS NOT OUR JOB. look.rot is already baked into `source` by the time it
 * reaches here (see look.js); this module only ever grades pixels.
 *
 * Backends, in order: WebGL2, WebGL1 (the same GLSL 1.00 shader serves both), then
 * a 2D-canvas pixel loop. applyLookToPixels() below IS that fallback, and it is a
 * line-for-line transliteration of the shader — they must stay in step.
 *
 * ORDER OF OPERATIONS (identical in both backends):
 *   0. sRGB -> linear
 *   1. temperature / tint   — linear channel gains, luma-normalised
 *   2. exposure             — linear multiply, in stops
 *   3. linear -> display (sRGB transfer); everything below is display-referred
 *   4. contrast             — non-clipping S-curve pivoted on mid-grey
 *   5. highlights           — luma-weighted lift, smooth rolloff
 *   6. shadows              — the same, weighted to the low end
 *      (5 and 6 weigh themselves on the luma from step 3, before the contrast
 *       curve: keyed off the curved tone instead, a steep curve folds them back)
 *   7. whites / blacks      — endpoint remap, each tapering to the far end
 *   8. saturation           — toward/away from luma
 *   9. vibrance             — saturation weighted toward the less saturated pixels
 *  10. clamp, ordered dither, 8-bit
 *
 * White balance and exposure sit in LINEAR light on purpose: channel gains applied
 * after the tone curve tilt the neutral axis and re-clip what the curve just
 * recovered. Everything tonal is display-referred, which is where the slider
 * labels ("punchier", "lifted") actually mean what they say.
 */
import { isNeutralAdj, normalizeAdj } from './look.js';

/* ---------------- parameters ---------------- */

/**
 * The sliders (-100..100) as the shader's uniforms (-1..1 plus derived gains).
 * Pure, so the numbers can be checked without a canvas.
 */
export function adjUniforms(adj) {
  const a = normalizeAdj(adj);
  const tp = a.tp / 100, tn = a.tn / 100;
  // Channel gains in stops. Warm lifts red and drops blue; tint trades green
  // against magenta. Normalised by luma so white balance never doubles as exposure.
  const gr = 2 ** (tp * 0.3 + tn * 0.08);
  const gg = 2 ** (-tn * 0.2);
  const gb = 2 ** (-tp * 0.3 + tn * 0.08);
  const norm = gr * 0.2126 + gg * 0.7152 + gb * 0.0722;
  return {
    wb: [gr / norm, gg / norm, gb / norm],
    exposure: a.ex / 100,
    contrast: a.ct / 100,
    highlights: a.hi / 100,
    shadows: a.sh / 100,
    whites: a.wh / 100,
    blacks: a.bl / 100,
    sat: a.sa / 100,
    vib: a.vb / 100,
  };
}

/* ---------------- the shader ---------------- */

// The flip lives here, not in UNPACK_FLIP_Y_WEBGL: that flag is ignored for an
// ImageBitmap source (its orientation is fixed when the bitmap is made), so a
// canvas and a bitmap of the same pixels came out of the shader one upside down
// from the other. Uploading everything unflipped and reading the texture bottom
// up is the one rule every source obeys.
const VERT = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
  v_uv = vec2(a_pos.x * 0.5 + 0.5, 0.5 - a_pos.y * 0.5);
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

// GLSL ES 1.00 so one shader serves WebGL1 and WebGL2.
const FRAG = `
precision highp float;
varying vec2 v_uv;
uniform sampler2D u_tex;
uniform vec3 u_wb;
uniform float u_exposure, u_contrast, u_highlights, u_shadows, u_whites, u_blacks, u_sat, u_vib;
uniform vec2 u_size;

const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);

vec3 toLinear(vec3 c) {
  return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(0.04045, c));
}
vec3 toSrgb(vec3 c) {
  c = max(c, 0.0);
  return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}
// smoothstep's own inverse: the pair gives a contrast that never clips either end.
float unSmooth(float x) {
  x = clamp(x, 0.0, 1.0);
  return 0.5 - sin(asin(1.0 - 2.0 * x) / 3.0);
}
// 4x4 ordered dither, +/- half a code: stretched tones would band without it.
float bayer(vec2 p) {
  vec2 c = mod(floor(p), 4.0);
  float i = c.y * 4.0 + c.x;
  float b = 0.0;
  b += mod(i, 2.0) * 8.0;
  b += mod(floor(i / 4.0), 2.0) * 4.0;
  b += mod(floor(i / 2.0), 2.0) * 2.0;
  b += floor(i / 8.0);
  return b / 16.0 - 0.5;
}

void main() {
  vec4 px = texture2D(u_tex, v_uv);
  vec3 lin = toLinear(clamp(px.rgb, 0.0, 1.0));

  lin *= u_wb;                                  // 1. white balance, in linear light
  lin *= exp2(u_exposure * 1.5);                // 2. exposure, +/- 1.5 stops

  vec3 d = clamp(toSrgb(lin), 0.0, 2.0);        // 3. display-referred from here down
  // The highlight/shadow masks key off the tone BEFORE the contrast curve: a
  // steep curve would otherwise swing the mask faster than the curve rises and
  // fold the ramp back on itself.
  float l = dot(clamp(d, 0.0, 1.0), LUMA);

  // 4. contrast, pivoted on mid-grey, both directions non-clipping
  if (u_contrast != 0.0) {
    vec3 s;
    if (u_contrast > 0.0) {
      vec3 t = clamp(d, 0.0, 1.0);
      s = t * t * (3.0 - 2.0 * t);
    } else {
      s = vec3(unSmooth(d.r), unSmooth(d.g), unSmooth(d.b));
    }
    d = mix(d, s, abs(u_contrast) * 0.8);
  }

  // 5/6. highlights and shadows: luma-weighted lifts. The signed form (toward
  // white when positive, toward black when negative) can never leave 0..1, so
  // there is no hard clip to band on. The 0.3 ceiling and the wide masks are not
  // taste: a steeper mask makes the curve fold back on itself near the end it
  // works on, which shows up as a reversed gradient in a sky.
  float wH = smoothstep(0.35, 1.0, l);
  float aH = u_highlights * 0.3;
  d += aH >= 0.0 ? aH * wH * (1.0 - d) : aH * wH * d;
  float wS = 1.0 - smoothstep(0.0, 0.7, l);
  float aS = u_shadows * 0.3;
  d += aS >= 0.0 ? aS * wS * (1.0 - d) : aS * wS * d;

  // 7. whites / blacks: endpoint remap. Each is a gain that ramps to its own end
  // and vanishes at the other, so brightening the whites does not also lift the
  // quarter tones the way a plain white-point divide would.
  d += u_whites * 0.22 * d;
  d -= u_blacks * 0.22 * (1.0 - d);

  // 8. saturation
  float l2 = dot(clamp(d, 0.0, 1.0), LUMA);
  d = mix(vec3(l2), d, 1.0 + u_sat);

  // 9. vibrance: the same move, weighted away from what is already saturated
  if (u_vib != 0.0) {
    vec3 t = clamp(d, 0.0, 1.0);
    float mx = max(t.r, max(t.g, t.b));
    float mn = min(t.r, min(t.g, t.b));
    float w = 1.0 - (mx > 0.0001 ? (mx - mn) / mx : 0.0);
    float l3 = dot(t, LUMA);
    d = mix(vec3(l3), d, 1.0 + u_vib * w);
  }

  d = clamp(d, 0.0, 1.0) + bayer(v_uv * u_size) / 255.0;
  gl_FragColor = vec4(clamp(d, 0.0, 1.0), px.a);
}`;

/* ---------------- the CPU transliteration ---------------- */

const LR = 0.2126, LG = 0.7152, LB = 0.0722;
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

function toLinear1(c) {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
function toSrgb1(c) {
  const v = c < 0 ? 0 : c;
  return v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;
}
function smooth1(x) {
  const t = clamp01(x);
  return t * t * (3 - 2 * t);
}
function unSmooth1(x) {
  return 0.5 - Math.sin(Math.asin(1 - 2 * clamp01(x)) / 3);
}
function smoothstep1(e0, e1, x) {
  return smooth1((x - e0) / (e1 - e0));
}
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
function bayer1(x, y) {
  return BAYER[(y & 3) * 4 + (x & 3)] / 16 - 0.5;
}

/**
 * One pixel, exactly as the shader does it. rgb in 0..1, returns 0..1.
 * Exported so the order of operations can be asserted without a browser.
 */
export function gradePixel(r, g, b, u) {
  let lr = toLinear1(clamp01(r)) * u.wb[0];
  let lg = toLinear1(clamp01(g)) * u.wb[1];
  let lb = toLinear1(clamp01(b)) * u.wb[2];
  const ev = 2 ** (u.exposure * 1.5);
  lr *= ev; lg *= ev; lb *= ev;

  let dr = Math.min(2, toSrgb1(lr)), dg = Math.min(2, toSrgb1(lg)), db = Math.min(2, toSrgb1(lb));
  const l = clamp01(dr) * LR + clamp01(dg) * LG + clamp01(db) * LB;

  if (u.contrast !== 0) {
    const m = Math.abs(u.contrast) * 0.8;
    const f = u.contrast > 0 ? smooth1 : unSmooth1;
    dr += (f(dr) - dr) * m;
    dg += (f(dg) - dg) * m;
    db += (f(db) - db) * m;
  }

  const wH = smoothstep1(0.35, 1, l);
  const aH = u.highlights * 0.3;
  if (aH >= 0) { dr += aH * wH * (1 - dr); dg += aH * wH * (1 - dg); db += aH * wH * (1 - db); }
  else { dr += aH * wH * dr; dg += aH * wH * dg; db += aH * wH * db; }
  const wS = 1 - smoothstep1(0, 0.7, l);
  const aS = u.shadows * 0.3;
  if (aS >= 0) { dr += aS * wS * (1 - dr); dg += aS * wS * (1 - dg); db += aS * wS * (1 - db); }
  else { dr += aS * wS * dr; dg += aS * wS * dg; db += aS * wS * db; }

  const aW = u.whites * 0.22, aB = u.blacks * 0.22;
  dr += aW * dr - aB * (1 - dr);
  dg += aW * dg - aB * (1 - dg);
  db += aW * db - aB * (1 - db);

  const l2 = clamp01(dr) * LR + clamp01(dg) * LG + clamp01(db) * LB;
  const sa = 1 + u.sat;
  dr = l2 + (dr - l2) * sa; dg = l2 + (dg - l2) * sa; db = l2 + (db - l2) * sa;

  if (u.vib !== 0) {
    const tr = clamp01(dr), tg = clamp01(dg), tb = clamp01(db);
    const mx = Math.max(tr, tg, tb), mn = Math.min(tr, tg, tb);
    const w = 1 - (mx > 0.0001 ? (mx - mn) / mx : 0);
    const l3 = tr * LR + tg * LG + tb * LB;
    const vs = 1 + u.vib * w;
    dr = l3 + (dr - l3) * vs; dg = l3 + (dg - l3) * vs; db = l3 + (db - l3) * vs;
  }

  return [clamp01(dr), clamp01(dg), clamp01(db)];
}

/**
 * The fallback backend: grade an ImageData in place. Also the reference the GL
 * path is tested against. `img` is anything with { data, width } — RGBA bytes.
 */
export function applyLookToPixels(img, look) {
  const adj = look && look.adj ? look.adj : look;
  if (isNeutralAdj(adj)) return img;
  const u = adjUniforms(adj);
  const { data, width } = img;
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const out = gradePixel(data[i] / 255, data[i + 1] / 255, data[i + 2] / 255, u);
    const d = bayer1(p % width, (p / width) | 0) / 255;
    data[i] = Math.round(clamp01(out[0] + d) * 255);
    data[i + 1] = Math.round(clamp01(out[1] + d) * 255);
    data[i + 2] = Math.round(clamp01(out[2] + d) * 255);
  }
  return img;
}

/* ---------------- the shared GL context ---------------- */

let state = null;   // { gl, prog, tex, loc, canvas } once built
let backend = null; // 'webgl2' | 'webgl' | 'cpu'

function compile(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

function buildGL() {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  // preserveDrawingBuffer so a caller can still copy the result out after an
  // await; without it the buffer is cleared at the next composite.
  const opts = {
    alpha: true, premultipliedAlpha: false, antialias: false, depth: false,
    stencil: false, preserveDrawingBuffer: true, powerPreference: 'low-power',
  };
  let gl = null;
  try { gl = canvas.getContext('webgl2', opts); } catch { gl = null; }
  if (gl) backend = 'webgl2';
  if (!gl) {
    try { gl = canvas.getContext('webgl', opts) || canvas.getContext('experimental-webgl', opts); } catch { gl = null; }
    if (gl) backend = 'webgl';
  }
  if (!gl) return null;

  const vs = compile(gl, gl.VERTEX_SHADER, VERT);
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
  if (!vs || !fs) return null;
  const prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
  // bound rather than called as gl.useProgram(): the linter reads any .useX() as
  // a React hook and refuses it after an early return
  const activate = gl.useProgram.bind(gl);
  activate(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(prog, 'a_pos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);

  const names = ['u_tex', 'u_wb', 'u_exposure', 'u_contrast', 'u_highlights', 'u_shadows',
    'u_whites', 'u_blacks', 'u_sat', 'u_vib', 'u_size'];
  const loc = {};
  names.forEach((n) => { loc[n] = gl.getUniformLocation(prog, n); });

  // A lost context comes back as silently-black frames; drop to the pixel loop.
  canvas.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    state = null;
    backend = 'cpu';
  });

  return { gl, prog, tex, loc, canvas, lastSrc: null, maxTex: gl.getParameter(gl.MAX_TEXTURE_SIZE) || 4096 };
}

function ensure() {
  if (state) return state;
  if (backend === 'cpu') return null;
  state = buildGL();
  if (!state) backend = 'cpu';
  return state;
}

/** Which backend is live: 'webgl2' | 'webgl' | 'cpu' (null until first use). */
export function pipelineBackend() {
  return backend;
}

/** Drops the context, so a test (or a context loss) can start clean. */
export function resetPipeline() {
  state = null;
  backend = null;
}

function sizeOf(src) {
  return { w: src.naturalWidth || src.width || 0, h: src.naturalHeight || src.height || 0 };
}

function drawGL(st, source, u, w, h) {
  const { gl, loc } = st;
  if (st.canvas.width !== w || st.canvas.height !== h) {
    st.canvas.width = w;
    st.canvas.height = h;
  }
  gl.viewport(0, 0, w, h);
  gl.bindTexture(gl.TEXTURE_2D, st.tex);
  // An ImageBitmap cannot change behind our back, so a slider dragging over the
  // same frame re-uploads nothing. Anything else might have been redrawn in
  // place, so it is always uploaded again.
  const cached = typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap && source === st.lastSrc;
  if (!cached) {
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    } catch {
      st.lastSrc = null;
      return false;               // a tainted or zero-sized source
    }
    st.lastSrc = source;
  }
  gl.uniform1i(loc.u_tex, 0);
  gl.uniform3fv(loc.u_wb, u.wb);
  gl.uniform1f(loc.u_exposure, u.exposure);
  gl.uniform1f(loc.u_contrast, u.contrast);
  gl.uniform1f(loc.u_highlights, u.highlights);
  gl.uniform1f(loc.u_shadows, u.shadows);
  gl.uniform1f(loc.u_whites, u.whites);
  gl.uniform1f(loc.u_blacks, u.blacks);
  gl.uniform1f(loc.u_sat, u.sat);
  gl.uniform1f(loc.u_vib, u.vib);
  gl.uniform2f(loc.u_size, w, h);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.drawArrays(gl.TRIANGLES, 0, 3);
  return !gl.isContextLost();
}

// Every backend ends the same way: `target` holds the graded pixels at w x h.
function paint(target, source, adj, w, h) {
  const u = adjUniforms(adj);
  const st = sizeOf(source).w ? ensure() : null;
  const g = target.getContext('2d');
  if (st && Math.max(sizeOf(source).w, sizeOf(source).h) <= st.maxTex && drawGL(st, source, u, w, h)) {
    g.clearRect(0, 0, w, h);
    g.drawImage(st.canvas, 0, 0);
    return true;
  }
  // fallback: scale with the 2D canvas, then walk the pixels
  try {
    g.clearRect(0, 0, w, h);
    g.imageSmoothingQuality = 'high';
    g.drawImage(source, 0, 0, w, h);
  } catch {
    return false;                 // a bitmap closed underneath us
  }
  try {
    const img = g.getImageData(0, 0, w, h);
    applyLookToPixels(img, adj);
    g.putImageData(img, 0, 0);
  } catch {
    return false;                 // a tainted canvas: the ungraded scale stands
  }
  return true;
}

/**
 * The look applied to `source`, as a plain 2D canvas of width x height.
 *
 * `source` must already be at (or near) the output size: the pipeline does not
 * resample beyond a bilinear fetch. Rotation is expected to be baked in already.
 *
 * Returns null when there is no canvas at all AND when the grade could not be
 * applied. paint() draws the ungraded source into the target before it reads the
 * pixels back, so on that path `out` holds UNGRADED pixels: handing them back
 * would be a caller saving a file that quietly ignores every slider. Callers
 * decide what to do with the null; the export fails the slide.
 */
export function renderLook(source, look, { width, height } = {}) {
  if (typeof document === 'undefined' || !source) return null;
  const s = sizeOf(source);
  const w = Math.max(1, Math.round(width || s.w));
  const h = Math.max(1, Math.round(height || s.h));
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const adj = look && look.adj ? look.adj : look;
  if (isNeutralAdj(adj)) {
    // Nothing to grade: stay bit-for-bit on the plain draw path.
    const g = out.getContext('2d');
    g.imageSmoothingQuality = 'high';
    g.drawImage(source, 0, 0, w, h);
    return out;
  }
  return paint(out, source, adj, w, h) ? out : null;
}

/**
 * Redraw an existing canvas in place, for a slider being dragged. The canvas
 * keeps its own size; one shared GL pass plus a blit is a frame's work.
 * Returns false when nothing could be drawn.
 */
export function renderLive(canvas, source, look) {
  if (!canvas || !source) return false;
  const w = canvas.width, h = canvas.height;
  if (!w || !h) return false;
  const adj = look && look.adj ? look.adj : look;
  if (isNeutralAdj(adj)) {
    const g = canvas.getContext('2d');
    if (!g) return false;
    g.clearRect(0, 0, w, h);
    g.imageSmoothingQuality = 'high';
    g.drawImage(source, 0, 0, w, h);
    return true;
  }
  return paint(canvas, source, adj, w, h);
}
